<template>
    <div class="ad-set-row">
        <div class="ad-set-columns row">
            <!-- Name -->
            <div class="col-xs-2 ad-set-name">
                <div v-if="canEditAdSet()" class="ad-set-links">
                    <a v-tooltip.top="'Edit'" :href="`/campaigns/${campaignId}/adset/${adSet.id}/edit`" style="text-decoration: none;">
                        {{ adSet.name }} {{ hasRole(['staff', 'engineer']) ? `(ID: ${adSet.id})` : '' }}
                    </a>
                </div>
                <div v-else>
                    {{ adSet.name }}
                </div>
                <span class="text-muted" title="type">{{ adSet.type.label }}</span>
                <span class="schedule" title="start date">{{ adSet.start_date | dateRange(adSet.end_date) }}</span>
            </div>

            <!-- On/Off -->
            <div v-if="canEditAdSet()" class="col-xs-1" v-tooltip.right="getActivationError">
                <br-switch :disabled="!canActivate"
                           :value="adSet.is_active"
                           @input="toggleActivation"
                           class="activation-switch">
                </br-switch>
            </div>
            <div v-else class="col-xs-1">
                {{ adSet.is_active ? 'Active' : 'Inactive' }}
            </div>

            <!-- Schedule On/Off -->
            <div v-if="showScheduleToggle" class="col-xs-1">
                <br-switch :value="!adSet.ignore_schedule"
                           @input="toggleIgnoreSchedule"
                           class="activation-switch">
                </br-switch>
            </div>

            <div class="col-xs-1">
                <div v-if="showLiveStatus">
                    <span v-if="loadingLiveStatus" class="content-loader">
                        <content-loader></content-loader>
                    </span>
                    <span v-else>
                        <pulse-status :status="isLive" :show="!isTestMode" onMessage="Live" :url="`/tools/ad-set-trace?adSetId=${adSet.id}`"/>
                        <pulse-status :status="true" :show="isTestMode"  onMessage="TestMode" :url="`/tools/ad-set-trace?adSetId=${adSet.id}`" class="testmode"/>
                    </span>
                </div>
            </div>

            <!-- Product Type -->
            <div class="col-xs-1" v-if="showProductType">{{ productType }}</div>

            <!-- Today / Leads -->
            <div class="col-xs-1">{{ formatNumber(adSet.daily_count) }}</div>

            <!-- Today / Spend -->
            <div class="col-xs-1">{{ formatMoney(adSet.daily_spend) }}</div>

            <!-- Today / Budget -->
            <div class="col-xs-1">{{ formattedBudget }}</div>

            <!-- Selected Date Range / Leads -->
            <div class="col-xs-1">
                <span v-show="loading" class="content-loader">
                    <content-loader></content-loader>
                </span>
                <span v-show="!loading">
                    {{ formatNumber(adSet.click_count) }}
                </span>
            </div>

            <!-- Selected Date Range / Spend -->
            <div class="col-xs-1">
                <span v-show="loading" class="content-loader">
                    <content-loader></content-loader>
                </span>
                <span v-show="!loading">
                    {{ formatMoney(adSet.cost) }}
                </span>
            </div>

            <!-- Selected Date Range / Position -->
            <div class="col-xs-1">
                <span v-show="loading" class="content-loader">
                    <content-loader></content-loader>
                </span>
                <span v-show="!loading">
                    {{ formatNumber(adSet.placement) }}
                </span>
            </div>

            <!-- Actions -->
            <div class="actions col-xs-1 text-center">
                    <span v-if="canEditAdSet()" class="delete-btn" v-tooltip.top="'Clone'" @click.prevent="$emit('clone', adSet.id)">
                        <i class="fa fa-files-o"></i>
                    </span>
                    <span v-if="canEditAdSet()" class="delete-btn" v-tooltip.top="'Delete'" @click.prevent="$emit('delete', adSet.id)">
                        &#10005;
                    </span>
                <div v-else>
                    <a :href="`/campaigns/${campaignId}/adset/${adSet.id}/preview`">Summary</a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import BidRow from './bid-row'
import {mapGetters} from "vuex";
import ContentLoader from 'js/components/utils/content-loader'
import {formatNumber, isBeforeStartOfDay, dayOfWeek} from 'js/utils'

export default {
    name: 'ad-set-row',

    props: {
        adSet: Object,
        campaignId: [String, Number],
        campaign: Object,
        showProductType: Boolean,
        loading: Boolean,
        isLive: Boolean,
        isTestMode: Boolean,
        showLiveStatus: Boolean,
        loadingLiveStatus: Boolean
    },

    data () {
        return {
            bidsVisible: false
        }
    },

    computed: {
        formattedBudget () {
            if (!this.adSet.budgets.length) {
                return ''
            }
            const [{budget_interval, budget}] = this.adSet.budgets;
            if (budget_interval === '9') {
                return 'Open'
            }

            if (budget_interval === '8') {
                return `$${formatNumber(budget)}.00`
            }

            const searchDay = isBeforeStartOfDay() ? dayOfWeek() - 1 : dayOfWeek();
            const amount = this.adSet.budgets.find(e => e.budget_interval == searchDay).budget;
            return `$${formatNumber(amount)}.00`
        },

        productType() {
            const productType = this.adSet.loan_purposes.length ? this.adSet.loan_purposes[0].label : null;

            switch (productType) {
                case 'Certificate of Deposit': {
                    return 'CD';
                }
                case 'Money Market Account': {
                    return 'MMA';
                }
                default: {
                    return productType;
                }
            }
        },

        canActivate() {
            return this.getActivationError == '';
        },

        showScheduleToggle () {
            return this.featureFlag('ad-set-ignore-schedule') && this.isBankrateSelect;
        },

        getActivationError() {
            let activationErrors = [];

            if (this.requiresCreative && this.adSet.creative_uuid == null) {
                activationErrors.push('a creative');
            }

            if (typeof this.adSet.start_date === 'undefined' || this.adSet.start_date == null) {
                activationErrors.push('a start date');
            }

            if (typeof this.adSet.budget_total === 'undefined' || this.adSet.budget_interval == null) {
                activationErrors.push('a budget');
            }

            if (activationErrors.length > 0) {
                return 'To activate an ad set you must have ' + activationErrors.join(' and ') + ' for the ad set.';
            } else {
                return '';
            }
        },

        requiresCreative() {
            return (
                this.productType == "HELOC" ||
                this.productType == "Home Equity Loan" ||
                this.productType == "Home Co-Investing" ||
                this.productType == "Cash Out Refinance"
            );
        },

        ...mapGetters([
            'isCostPerClick',
            'isCostPerLead',
            'isBankrateSelect'
        ])
    },

    methods: {
        toggleBids () {
            this.bidsVisible = this.isCostPerClick ? !this.bidsVisible : false
        },

        toggleActivation () {
            const event = this.adSet.is_active
                ? 'deactivate'
                : 'activate';
            this.$emit(event, this.adSet.id)
        },

        toggleIgnoreSchedule () {
            const event = this.adSet.ignore_schedule
                ? 'ignore-schedule-enabled'
                : 'ignore-schedule-disabled';
            this.$emit(event, this.adSet.id)
        },

        changeBidStatus (bidId) {
            this.$emit('change-bid-status', bidId)
        },

        canEditAdSet: function() {
            if (this.campaign.is_advertiser_edit_blocked && !this.hasRole(['staff', 'engineer'])) {
                return false
            }

            return this.canEditWithSelect(this.campaign.blnBankrateSelect)
        },

        formatMoney: function(value) {
            if (isNaN(value) || value == null) {
                return '-'
            }

            return '$' + this.$options.filters.currency(value)
        },

        formatNumber: function(value) {
            if (isNaN(value) || value == null) {
                return '-'
            }

            return this.$options.filters.number(value);
        }
    },

    components: {
        BidRow,
        ContentLoader
    }
}
</script>

<style lang="scss" scoped>
.actions {
    padding: 0;
    justify-content: space-around;
}

.ad-set-row {
    font-size: 15px;
    border-bottom: 1px solid #dedede;
}

.ad-set-columns {
    display: flex;
    position: relative;

    > div {
        min-height: 65px;
        padding-bottom: 15px;
        padding-top: 15px;
        align-items: center;
        display: flex;
    }

    .ad-set-name {
        align-items: flex-start;
        flex-direction: column;
    }
}

.bid-count {
    cursor: pointer;
    user-select: none;
}

.bid-count .glyphicon {
    font-size: 9px;
}

.bid-count.expanded:after {
    background-color: #f7f7fa;
    border-left: solid 1px #dedede;
    border-top: solid 1px #dedede;
    bottom: 0;
    content: '';
    display: block;
    height: 20px;
    position: absolute;
    transform: rotate(45deg) translate(30%, 45%);
    width: 20px;
}

.schedule {
    font-size: 12px;
}

.delete-btn {
    color: rgba(#a7a8af, .5);
    cursor: pointer;
    font-size: 20px;
    padding: 0 5px;

    &:hover {
        color: #6f707b;
    }
}

.ad-set-links {
    align-items: center;
    border: 1px solid transparent;
    cursor: pointer;
    display: flex;
    width: 100%;

    &:hover {
        border-color: #337ab7;
        text-decoration: none;

        .fa {
            background-color: #337ab7;
            color: white;
            line-height: 21px;
            padding-left: 1px;
        }
    }

    .fa {
        color: transparent;
    }
}

.edit-link {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 90%;

    &:hover {
        text-decoration: none;
    }
}

.slide-down-enter-active {
    transition: all .5s ease-in;
}

.slide-down-leave-active {
    transition: all .25s ease;
}

.slide-down-enter, .slide-down-leave-to, .slide-down-leave-active {
    max-height: 0 !important;
}

.bid-group {
    overflow-y: hidden;
    max-height: 1000px;
}

.content-loader svg {
    height: 15px;
    margin-left: -3px;
}
</style>
